/***** Response Types *****/
type SchoolHoursResponse = Array<{
  "school-hours/start": number;
  "school-hours/end": number;
  "school-hours/day": ShortenedDays;
}>;

/***** Model Types *****/
export type ShortenedDays = "mon" | "tue" | "wed" | "thu" | "fri";

export type SchoolHours = {
  day: ShortenedDays;
  start: number;
  end: number;
};

/***** Constants *****/
export const DEFAULT_SCHOOL_START_TIME = { hour: 7, minute: 30, seconds: 0 };
export const DEFAULT_SCHOOL_END_TIME = { hour: 16, minute: 30, seconds: 0 };

const DEFAULT_SCHOOL_HOURS: Array<SchoolHours> = [
  {
    day: "mon",
    start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
    end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
  },
  {
    day: "tue",
    start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
    end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
  },
  {
    day: "wed",
    start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
    end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
  },
  {
    day: "thu",
    start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
    end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
  },
  {
    day: "fri",
    start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
    end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
  },
];

/**
 * Model | SchoolHoursModel
 */
export class SchoolHoursModel {
  schoolHours = DEFAULT_SCHOOL_HOURS;

  constructor(schoolHours?: SchoolHoursResponse | Array<SchoolHours>) {
    if (!schoolHours || schoolHours.length === 0) return;

    if ("school-hours/day" in schoolHours[0]) {
      this.schoolHours = (schoolHours as SchoolHoursResponse).map((item) => ({
        start: item["school-hours/start"],
        end: item["school-hours/end"],
        day: item["school-hours/day"],
      }));
    } else {
      this.schoolHours = schoolHours as Array<SchoolHours>;
    }
  }

  getDefaultHours(day: ShortenedDays) {
    return {
      start: DEFAULT_SCHOOL_START_TIME.hour * 100 + DEFAULT_SCHOOL_START_TIME.minute,
      end: DEFAULT_SCHOOL_END_TIME.hour * 100 + DEFAULT_SCHOOL_END_TIME.minute,
      day: day,
    };
  }

  get mon() {
    return this.schoolHours.find(({ day }) => day === "mon") ?? this.getDefaultHours("mon");
  }

  get tue() {
    return this.schoolHours.find(({ day }) => day === "tue") ?? this.getDefaultHours("tue");
  }

  get wed() {
    return this.schoolHours.find(({ day }) => day === "wed") ?? this.getDefaultHours("wed");
  }

  get thu() {
    return this.schoolHours.find(({ day }) => day === "thu") ?? this.getDefaultHours("thu");
  }

  get fri() {
    return this.schoolHours.find(({ day }) => day === "fri") ?? this.getDefaultHours("fri");
  }

  // Used to edit school hours in School Profile table
  //
  // The returned data has Clojure key/value pairs because it's
  // used with `new SchoolModel()` to regenerate a new model structure.
  //
  // The base data source which we are manipulating all our values
  // off of is the clojure data structure - so while we could technically
  // change the clojure keys themselves within EditableHours, it's easier
  // to just generate Clojure keys here and then re-create a model in
  // other places.
  updateHours(newSchoolHours: SchoolHours) {
    const schoolHoursCopy = [...this.schoolHours];
    const indexOfDayItem = this.schoolHours.findIndex(({ day }) => day === newSchoolHours.day);

    if (indexOfDayItem === -1) {
      schoolHoursCopy.push(newSchoolHours);
    } else {
      schoolHoursCopy[indexOfDayItem] = newSchoolHours;
    }

    return schoolHoursCopy;
  }
}
